<script setup>
import { computed, inject, onMounted, onUnmounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required as defaultRequired, email as defaultEmail, helpers } from '@vuelidate/validators';
import { CdrText, CdrInput, CdrButton } from '@rei/cedar';
import { useCancellationStore } from '../../stores/day-cancellation';
import WheresMyReservationId from './WheresMyReservationId.vue';
import CancellationRefundPolicy from './CancellationRefundPolicy.vue';
import cms from './data';

const cancellation = useCancellationStore();

const required = helpers.withMessage('Required field', defaultRequired);
const email = helpers.withMessage('Invalid email', defaultEmail);

const rules = computed(() => ({
  firstName: { required },
  lastName: { required },
  reservationId: { required },
  email: { required, email },
}));

const v$ = useVuelidate(rules, cancellation);

const isDisabled = computed(() => v$.value.$invalid);

const updateStore = (key, value) => {
  cancellation[key] = value;
};

const blurField = (key) => {
  v$.value[key].$touch();
};

const getErrorMessage = (key) => {
  if (v$.value[key].$invalid && !!v$.value[key].$errors[0]) {
    return v$.value[key].$errors[0].$message;
  }
  return false;
};

const metricsClick = inject('metricsClick');

const handleSubmit = () => {
  metricsClick('ReservationLookup', 'submit');
  cancellation.lookupReservation();
};

const handleGlobalSubmit = (e) => {
  if (e.code === 'Enter' && e.target.localName === 'body' && !isDisabled.value) {
    handleSubmit();
  }
};

const prefillFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('fn')) cancellation.firstName = params.get('fn');
  if (params.get('ln')) cancellation.lastName = params.get('ln');
  if (params.get('em')) cancellation.email = params.get('em');
  if (params.get('rn')) cancellation.reservationId = params.get('rn');
};

onMounted(() => {
  document.addEventListener('keyup', handleGlobalSubmit);
  prefillFromUrl();
});

onUnmounted(() => {
  document.removeEventListener('keyup', handleGlobalSubmit);
});
</script>

<template>
  <section class="reservation-lookup">
    <CdrText
      v-if="cancellation.isStepError"
      class="reservation-lookup__error"
      data-ui="reservation-lookup-error"
    >
      <span v-html="cms.reservationLookup.error" />
    </CdrText>
    <form
      class="reservation-lookup__form"
      @submit.prevent="handleSubmit"
    >
      <div class="reservation-lookup__name">
        <CdrInput
          input-container-class="reservation-lookup__input"
          label="Participant first name"
          maxlength="40"
          size="large"
          data-ui="reservation-lookup-first-name"
          :model-value="cancellation.firstName"
          :error="getErrorMessage('firstName')"
          required
          @update:model-value="(value) => updateStore('firstName', value)"
          @blur="blurField('firstName')"
        />
        <CdrInput
          input-container-class="reservation-lookup__input"
          label="Participant last name"
          maxlength="80"
          size="large"
          data-ui="reservation-lookup-last-name"
          :error="getErrorMessage('lastName')"
          :model-value="cancellation.lastName"
          required
          @update:model-value="(value) => updateStore('lastName', value)"
          @blur="blurField('lastName')"
        />
      </div>
      <div class="reservation-lookup__reservation-id">
        <CdrInput
          input-container-class="reservation-lookup__input"
          label="Reservation Number"
          maxlength="40"
          size="large"
          data-ui="reservation-lookup-reservation-id"
          required
          type="number"
          :numeric="true"
          :model-value="cancellation.reservationId"
          :error="getErrorMessage('reservationId')"
          @update:model-value="(value) => updateStore('reservationId', value)"
          @blur="blurField('reservationId')"
        >
          <template #pre-icon>
            <CdrText class="reservation-lookup_pre-fix">RES -</CdrText>
          </template>
        </CdrInput>
        <WheresMyReservationId />
      </div>
      <CdrInput
        input-container-class="reservation-lookup__input"
        label="Email"
        maxlength="80"
        size="large"
        data-ui="reservation-lookup-email"
        :model-value="cancellation.email"
        :error="getErrorMessage('email')"
        required
        @update:model-value="(value) => updateStore('email', value)"
        @blur="blurField('email')"
      />
      <CdrButton
        ref="submitBtn"
        class="reservation-lookup__button"
        size="large"
        data-ui="reservation-lookup-button"
        type="submit"
        :disabled="isDisabled"
      >
        {{ cms.reservationLookup.button }}
      </CdrButton>
    </form>
    <CancellationRefundPolicy />
  </section>
</template>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.reservation-lookup {
  max-width: 706px;

  &__error {
    @include cdr-text-body-300;
    color: $cdr-color-text-message-error;
    padding-top: $cdr-space-half-x;
  }

  &__form {
    padding-top: $cdr-space-two-x;
  }

  &__name,
  &__reservation-id {
    display: flex;
    flex-direction: column;
    gap: $cdr-space-one-x;
    padding-bottom: $cdr-space-two-x;
  }

  &_pre-fix {
    @include cdr-text-utility-sans-400;
  }

  &__input {
    width: 100%;
  }

  &__button {
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    margin-top: $cdr-space-two-x;
  }

  @include cdr-sm-mq-up {
    &__name {
      flex-direction: row;
      align-items: flex-start;
    }

    &__input {
      max-width: 343px;
    }

    &__button {
      width: fit-content;
    }
  }
}
</style>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '@rei/cdr-component-variables/dist/scss/index.scss';

.reservation-lookup {
  &__error {
    a {
      @include cdr-link-base-mixin;
      color: $cdr-color-text-message-error;

      &:hover {
        color: $cdr-color-text-message-error;
      }
    }
  }

  &__input input[data-ui='reservation-lookup-reservation-id'] {
    padding-left: 5.6rem !important;
  }
}
</style>
